/**
 * TTL-based, namespace-isolated options cache.
 * Lazy eviction: stale entries are removed on get().
 *
 * @param {number} [defaultTtl] - Default TTL in ms. Defaults to 5 minutes.
 * @returns {{ get(ns: string, key: string): unknown, set(ns: string, key: string, val: unknown, ttl?: number): void, has(ns: string, key: string): boolean, clear(ns: string): void, destroy(): void }}
 */
export function createOptionsCache(defaultTtl = 5 * 60_000) {
  /** @type {Map<string, Map<string, { value: unknown, expiresAt: number }>>} */
  const namespaces = new Map();

  /**
   * @param {string} ns
   * @returns {Map<string, { value: unknown, expiresAt: number }>}
   */
  function getNamespace(ns) {
    let nsMap = namespaces.get(ns);
    if (!nsMap) {
      nsMap = new Map();
      namespaces.set(ns, nsMap);
    }
    return nsMap;
  }

  /**
   * @param {string} ns
   * @param {string} key
   * @returns {boolean}
   */
  function has(ns, key) {
    const nsMap = namespaces.get(ns);
    if (!nsMap) return false;
    const entry = nsMap.get(key);
    if (!entry) return false;
    if (Date.now() >= entry.expiresAt) {
      nsMap.delete(key);
      return false;
    }
    return true;
  }

  /**
   * @param {string} ns
   * @param {string} key
   * @returns {unknown}
   */
  function get(ns, key) {
    const nsMap = namespaces.get(ns);
    if (!nsMap) return undefined;
    const entry = nsMap.get(key);
    if (!entry) return undefined;
    if (Date.now() >= entry.expiresAt) {
      nsMap.delete(key);
      return undefined;
    }
    return entry.value;
  }

  /**
   * @param {string} ns
   * @param {string} key
   * @param {unknown} val
   * @param {number} [ttl]
   */
  function set(ns, key, val, ttl) {
    const nsMap = getNamespace(ns);
    nsMap.set(key, { value: val, expiresAt: Date.now() + (ttl ?? defaultTtl) });
  }

  /**
   * @param {string} ns
   */
  function clear(ns) {
    namespaces.delete(ns);
  }

  function destroy() {
    namespaces.clear();
  }

  return { get, set, has, clear, destroy };
}
